import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { ErrorNote, Loading } from "../components/ui";
import type { ApprovedUser, Location, UploadBatchRow } from "../types";

export default function Administration() {
  const [users, setUsers] = useState<ApprovedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [oid, setOid] = useState("");
  const [role, setRole] = useState("Reader");
  const [displayName, setDisplayName] = useState("");

  const [locations, setLocations] = useState<Location[]>([]);
  const [locCode, setLocCode] = useState("");
  const [locName, setLocName] = useState("");
  const [locTz, setLocTz] = useState("Asia/Kolkata");
  const [uploads, setUploads] = useState<UploadBatchRow[]>([]);

  const load = () => {
    setLoading(true);
    api.adminUsers()
      .then((r) => { setUsers(r.users); setError(null); })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
    api.locations().then((r) => setLocations(r.locations)).catch(() => undefined);
    api.adminUploads().then((r) => setUploads(r.uploads)).catch(() => undefined);
  };
  useEffect(load, []);

  const deleteUpload = async (u: UploadBatchRow) => {
    const removesData = u.status === "completed";
    const prompt = removesData
      ? `Revert this upload?\n\nFile: ${u.filename}\nLocation: ${u.location}\nPeriod: ${u.reporting_period || "—"}\n\n`
        + `This REMOVES all records imported by this file from ${u.location}. `
        + `Use this if you uploaded to the wrong location or a wrong file. `
        + `You can re-upload a corrected file afterwards.`
      : `Remove this upload entry (${u.filename}, ${u.location})? It imported no records, so nothing else changes.`;
    if (!window.confirm(prompt)) return;
    try {
      const r = await api.deleteUpload(u.upload_id, u.location);
      load();
      if (r.daily_deleted || r.events_deleted)
        alert(`Reverted: ${r.daily_deleted} day rows and ${r.events_deleted} events removed from ${u.location}.`);
    } catch (e) { setError((e as ApiError).message); }
  };

  const addLocation = async () => {
    try {
      await api.addLocation(locCode.trim().toLowerCase(), locName.trim(), locTz.trim());
      setLocCode(""); setLocName(""); load();
    } catch (e) { setError((e as ApiError).message); }
  };
  const removeLocation = async (code: string) => {
    try { await api.removeLocation(code); load(); }
    catch (e) { setError((e as ApiError).message); }
  };

  const add = async () => {
    if (!oid.trim()) { setError("Object ID is required."); return; }
    try {
      await api.addUser(oid.trim(), role, displayName.trim());
      setOid(""); setDisplayName(""); load();
    } catch (e) { setError((e as ApiError).message); }
  };

  const remove = async (id: string) => {
    try { await api.removeUser(id); load(); }
    catch (e) { setError((e as ApiError).message); }
  };

  return (
    <div>
      <h1 className="h1">Administration</h1>
      <p className="sub">
        Primary access control is via Entra groups (sg-att-admin / sg-att-reader).
        This supplementary allowlist is enforced server-side in addition to group membership.
      </p>

      <div className="panel">
        <h2>Upload history</h2>
        <p className="notice"><b>Revert</b> undoes a mistaken upload — e.g. a file sent to the wrong location —
          by removing exactly that file's imported records, then re-upload the correct file. Only that upload's
          rows are removed; other uploads and historical months are untouched.</p>
        <table>
          <thead>
            <tr><th>File</th><th>Location</th><th>Format</th><th>Period</th><th>Status</th>
              <th>Accepted</th><th>Days</th><th></th></tr>
          </thead>
          <tbody>
            {uploads.map((u) => (
              <tr key={u.upload_id}>
                <td>{u.filename}</td>
                <td style={{ textTransform: "capitalize" }}>{u.location}</td>
                <td>{u.report_format || "—"}</td>
                <td>{u.reporting_period || "—"}</td>
                <td><span className={`tag ${u.status === "completed" ? "ok" : u.status === "deleted" ? "incomplete" : "review"}`}>{u.status}</span></td>
                <td>{u.accepted}</td>
                <td>{u.daily_records}</td>
                <td>{u.status === "deleted"
                  ? <span className="notice">reverted</span>
                  : <button className="ghost" style={{ color: "var(--incomplete)", borderColor: "var(--incomplete)" }}
                            onClick={() => deleteUpload(u)}>
                      {u.status === "completed" ? "Revert" : "Remove"}
                    </button>}</td>
              </tr>
            ))}
            {uploads.length === 0 && <tr><td colSpan={8} className="notice">No uploads yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h2>Office locations</h2>
        <div className="filters">
          <div className="field">
            <label>Code</label>
            <input value={locCode} onChange={(e) => setLocCode(e.target.value)} placeholder="e.g. bangalore" />
          </div>
          <div className="field">
            <label>Display name</label>
            <input value={locName} onChange={(e) => setLocName(e.target.value)} placeholder="e.g. Bangalore" />
          </div>
          <div className="field">
            <label>Time zone</label>
            <input value={locTz} onChange={(e) => setLocTz(e.target.value)} placeholder="Asia/Kolkata" />
          </div>
          <button className="primary" onClick={addLocation}>Add location</button>
        </div>
        <table style={{ marginTop: 12 }}>
          <thead><tr><th>Code</th><th>Name</th><th>Time zone</th><th></th></tr></thead>
          <tbody>
            {locations.map((l) => (
              <tr key={l.code}>
                <td>{l.code}</td>
                <td>{l.name}</td>
                <td>{l.timezone}</td>
                <td>{l.removable
                  ? <button className="ghost" onClick={() => removeLocation(l.code)}>Remove</button>
                  : <span className="notice">built-in</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h2>Add approved user</h2>
        <div className="filters">
          <div className="field">
            <label>Entra object ID</label>
            <input value={oid} onChange={(e) => setOid(e.target.value)} placeholder="00000000-0000-…" />
          </div>
          <div className="field">
            <label>Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)}>
              <option>Reader</option>
              <option>Administrator</option>
            </select>
          </div>
          <div className="field">
            <label>Display name</label>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </div>
          <button className="primary" onClick={add}>Add</button>
        </div>
        {error && <ErrorNote message={error} />}
      </div>

      <div className="panel">
        <h2>Approved users</h2>
        {loading ? <Loading what="users" /> : (
          <table>
            <thead>
              <tr><th>Object ID</th><th>Role</th><th>Name</th><th>Added by</th><th></th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.object_id}>
                  <td>{u.object_id}</td>
                  <td>{u.role}</td>
                  <td>{u.display_name}</td>
                  <td>{u.added_by}</td>
                  <td><button className="ghost" onClick={() => remove(u.object_id)}>Remove</button></td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={5} className="notice">No supplementary users.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
